import { calculateAfterpayPrice } from '../helpers/afterpay-price'

export class VariantSelector extends HTMLElement {
  private container: HTMLDivElement
  private select: HTMLSelectElement | null
  private dropdownButton: HTMLElement | null
  private sizeLabel: HTMLElement | null
  private options: NodeListOf<HTMLElement>
  private urlSync: boolean
  private pairFormId: string | null
  private priceSelector: string | null
  private afterpayPriceSelector: string | null

  private static SELECTED_BUTTON_CLASSES = ['active']

  constructor() {
    super()
    this.container = document.createElement('div')
    while (this.firstChild) {
      this.container.appendChild(this.firstChild)
    }
    Array.from(this.attributes).forEach(attr => {
      if (attr.name !== 'is') {
        this.container.setAttribute(attr.name, attr.value)
      }
    })
    this.appendChild(this.container)

    this.select = null
    this.dropdownButton = null
    this.sizeLabel = null
    this.urlSync = false
    this.pairFormId = null
    this.priceSelector = null
    this.afterpayPriceSelector = null
    this.options = this.container.querySelectorAll('[data-select-option]')
  }

  connectedCallback(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize())
    } else {
      this.initialize()
    }
  }

  private initialize(): void {
    this.urlSync = this.hasAttribute('data-url-sync')
    this.pairFormId = this.getAttribute('data-pair-form-id')
    this.priceSelector = this.getAttribute('data-price-selector')
    this.afterpayPriceSelector = this.getAttribute('data-afterpay-price-selector')
    this.select = this.parentNode ? this.parentNode.querySelector<HTMLSelectElement>('select[name="id"]') : null
    this.dropdownButton = this.container.querySelector<HTMLElement>('[data-modal-role="toggle"]')
    this.sizeLabel = this.dropdownButton?.querySelector<HTMLElement>('[size-selector-label') || null
    this.options = this.container.querySelectorAll<HTMLElement>('[data-select-option]')

    this.setupEventListeners()

    if (this.select && this.select.value) {
      const selectedOption = this.container.querySelector<HTMLElement>(`[data-variant-id="${this.select.value}"]`)
      if (selectedOption) {
        this.updatePriceDisplay(selectedOption)
        this.updateAfterpayPriceDisplay(selectedOption)
        this.updateSizeProperty(selectedOption)
      }
    }
  }

  private updatePriceDisplay(option: HTMLElement): void {
    if (this.priceSelector) {
      const priceElements = document.querySelectorAll<HTMLElement>(this.priceSelector)
      if (priceElements.length > 0) {
        const priceCurrent = option.dataset.variantPrice || ''
        const priceCompare = option.dataset.variantPriceCompare || ''

        priceElements.forEach(priceElement => {
          priceElement.style.setProperty('--price-current', `'${priceCurrent}'`)
          priceElement.style.setProperty('--price-compare', `'${priceCompare}'`)

          const onSale = priceCompare !== '' && priceCompare !== priceCurrent
          priceElement.setAttribute('data-sale', onSale.toString())
        })
      }
    }
  }

  private updateAfterpayPriceDisplay(option: HTMLElement): void {
    if (this.afterpayPriceSelector) {
      const afterpayElements = document.querySelectorAll<HTMLElement>(this.afterpayPriceSelector)
      if (afterpayElements.length > 0) {
        const priceString = option.dataset.variantPrice || ''
        const numericPrice = parseFloat(priceString.replace(/[^\d.]/g, ''))

        const afterpayPrice = calculateAfterpayPrice(numericPrice)

        if (afterpayPrice) {
          afterpayElements.forEach(afterpayElement => {
            afterpayElement.style.setProperty('--price-current', `'${afterpayPrice}'`)
          })
        }
      }
    }
  }

  private setupEventListeners(): void {
    this.options.forEach(option => {
      option.addEventListener('click', (event) => this.handleOptionSelect(event))
    })

    if (this.select) {
      this.select.addEventListener('change', () => {
        if (this.select && this.select.value) {
          const selectedOption = this.container.querySelector<HTMLElement>(`[data-variant-id="${this.select.value}"]`)
          if (selectedOption) {
            this.updatePriceDisplay(selectedOption)
            this.updateAfterpayPriceDisplay(selectedOption)
            this.updateSizeProperty(selectedOption)
          }
        }
      })
    }
  }

  private updatedSelectedState(option: HTMLElement): void {
    this.options.forEach(option => {
      option.classList.remove(...VariantSelector.SELECTED_BUTTON_CLASSES)
    })

    option.classList.add(...VariantSelector.SELECTED_BUTTON_CLASSES)
  }

  private handleOptionSelect(event: Event): void {
    const option = event.currentTarget as HTMLElement
    const variantId = option.dataset.variantId || ''
    const variantOption = option.dataset.variantOption || ''

    if (this.select) {
      this.select.value = variantId

      const changeEvent = new Event('change', { bubbles: true })
      this.select.dispatchEvent(changeEvent)
    }

    this.updatePriceDisplay(option)
    this.updateAfterpayPriceDisplay(option)

    if (this.pairFormId) {
      const pairedElement = document.getElementById(this.pairFormId)
      if (pairedElement) {
        const hiddenSelector = pairedElement.querySelector('variant-selector')
        if (hiddenSelector) {
          const matchingOption = hiddenSelector.querySelector<HTMLElement>(`[data-variant-id="${variantId}"]`)
          if (matchingOption) {
            matchingOption.click()
          }
        }
      }
    }

    this.updateSizeProperty(option)

    this.updatedSelectedState(option)
    if (this.sizeLabel) {
      this.sizeLabel.textContent = variantOption

      if (this.dropdownButton) {
        this.dropdownButton.classList.add(...VariantSelector.SELECTED_BUTTON_CLASSES)
      }
    }

    if (this.urlSync) {
      window.history.replaceState({}, '', '?variant=' + variantId)
    }

    const closeButton = this.container.querySelector<HTMLElement>('[data-modal-role="close"]')
    if (closeButton) closeButton.click()
  }

  private updateSizeProperty(option: HTMLElement): void {
    const form = this.select?.closest('form')
    if (!form) return

    const sizeMetricInput = form.querySelector('[data-quick-add-size-metric-input]') as HTMLInputElement
    if (!sizeMetricInput) return

    const hasUniversalSizing = option.hasAttribute('data-size-uk') ||
                                option.hasAttribute('data-size-us') ||
                                option.hasAttribute('data-size-eu') ||
                                option.hasAttribute('data-size-cm')

    if (!hasUniversalSizing) return

    const variantOption = option.dataset.variantOption || ''

    if (variantOption.startsWith('EU ')) {
      sizeMetricInput.value = variantOption.replace('EU ', 'EURO ')
    } else {
      sizeMetricInput.value = variantOption
    }
  }
}
